"use client";

import type { ServerError } from "@offstreetparking/design-system/forms";
import { createContext, useContext, useEffect } from "react";
import type { RouterOutputs } from "../api/types";

export type ParkerUser = RouterOutputs["parkers"]["getParkerSession"];

export type ParkerContextValue =
	| {
			parker: ParkerUser | (Omit<ParkerUser, "defaultVehicle"> & { defaultVehicle: null });
			displayName: string;
			error?: ServerError;
			authState: "authenticating" | "authenticated";
			isLoading: boolean;
			isInitialLoading: boolean;
			isLoggedIn: true;
			needsParkerCreation: boolean;
			isConnectedParker: boolean;
			globalParkerFlag: boolean;
			refreshAuth: () => Promise<ParkerUser>;
	  }
	| {
			parker?: never;
			displayName?: never;
			error?: ServerError;
			authState: "authenticating" | "unauthenticated";
			isLoading: boolean;
			isInitialLoading: boolean;
			isLoggedIn: false;
			needsParkerCreation: boolean;
			isConnectedParker: boolean;
			globalParkerFlag: boolean;
			refreshAuth: () => Promise<ParkerUser>;
	  };

const ParkerContext = createContext<ParkerContextValue | undefined>(undefined);

export const ParkerStateProvider = ({ value, children }: { value: ParkerContextValue; children: React.ReactNode }) => (
	<ParkerContext.Provider value={value}>{children}</ParkerContext.Provider>
);

type UseParkerOptions = {
	ensureSignedIn?: boolean;
	onUnauthorized?: () => void;
	onAuthorized?: () => void;
};

export const useParker = ({ ensureSignedIn, onUnauthorized, onAuthorized }: UseParkerOptions = {}) => {
	const context = useContext(ParkerContext);

	if (context === undefined) {
		throw new Error("useParker must be used within a ParkerStateProvider");
	}

	useEffect(() => {
		if (context.authState === "authenticated" && ensureSignedIn === false) {
			onAuthorized?.();
		}
		if (context.authState === "unauthenticated" && ensureSignedIn === true) {
			onUnauthorized?.();
		}
	}, [context.authState, ensureSignedIn, onAuthorized, onUnauthorized]);

	return context;
};
